/* eslint-disable */
import {createSlice, Draft, PayloadAction} from '@reduxjs/toolkit';
import {GroupCategoryType, ProductType} from 'app/features/Order/types/OrderTypes';
import {deleteKey, getKey, setKey} from "app/shared/utils/EncryptionUtility";
import {OrderSubmitType} from "app/features/Order/types/OrderSubmitType";
import {calculateTotal} from "app/shared/utils/helperFunctions";
import {AppliedRewards, FinalOrderResponseType} from "app/features/Order/types/FinalOrderResponseType";

const initialState: OrderState = {
    totalItems: getKey('totalItems') ? Number(getKey('totalItems')) : 0,
    itemsInCart: getKey('itemsInCart') ? JSON.parse(Object(getKey('itemsInCart'))) : [],
    itemSelectedForEdit: null,
    category: 'Featured',
    menuGroups: [],
    selectedGroup: 'All',
    finalOrder: {} as OrderSubmitType,
    orderNumber: '',
    reOrderNumber: '',
    orderStatus: 'ReadyForValidation',
    originalTotal: 0,
    totalPrice: getKey('totalPrice') ? Number(getKey('totalPrice')) : 0,
    totalCartPrice: getKey('totalCartPrice') ? Number(getKey('totalCartPrice')) : 0,
    tax: 0,
    youPay: 0,
    navBarButtonDisable: false,
    allGroups: [],
    enterpriseUnitId: '',
    isOrderLoading: false,
    appliedRewards: [],
    paymentMethod: '',
    paymentCardType: ''
};

interface OrderState {
    totalItems: number;
    itemsInCart: ProductType[];
    itemSelectedForEdit: ProductType | null;
    category: string;
    menuGroups: string[];
    selectedGroup: string | undefined;
    finalOrder: OrderSubmitType;
    orderNumber: string;
    reOrderNumber: string;
    orderStatus: string;
    originalTotal: number;
    totalPrice: number;
    totalCartPrice: number;
    tax: number;
    youPay: number;
    navBarButtonDisable: boolean;
    allGroups: GroupCategoryType[];
    enterpriseUnitId: string;
    appliedRewards: AppliedRewards[];
    isOrderLoading: boolean;
    paymentMethod: string;
    paymentCardType: string;
}

export type CategoryGroup = {
    category: string;
    subCategory: string | undefined;
};

export const orderSlice = createSlice({
    name: 'orderItems',
    initialState,
    reducers: {
        changeGroup: (state, action: PayloadAction<CategoryGroup>) => {
            if (action.payload.category !== 'noChange') {
                state.category = action.payload.category;
            }
            if (state.selectedGroup) {
                state.selectedGroup = action.payload.subCategory;
            }
        },
        updateMenuGroups: (state, action: PayloadAction<string[]>) => {
            state.menuGroups.push(...action.payload);
        },
        updateAllGroups: (state, action: PayloadAction<GroupCategoryType[]>) => {
            state.allGroups = action.payload
        },
        setItemSelectedForEdit: (state, action: PayloadAction<ProductType | null>) => {
            state.itemSelectedForEdit = action.payload
        },
        addItemsToCart: (state, action: PayloadAction<ProductType>) => {
            // find if the item exists in the card
            state.totalItems += 1;
            let payloadProdId = action.payload.omsData.salesItems[0].productId;
            let payloadProdSizeId = action.payload.productSizeId;
            let payloadSubProd = action.payload.subProducts;
            let jPaySubProd = JSON.stringify(payloadSubProd);
            const isItemInCart = state.itemsInCart.find(
                (item) => {
                    let {productId} = item.omsData.salesItems[0];
                    const {productSizeId, subProducts} = item;
                    const jSubProd = JSON.stringify(subProducts)
                    return (
                        ((productId === payloadProdId) && productSizeId === payloadProdSizeId && jPaySubProd === jSubProd)
                    )
                }
            );

            if (isItemInCart) {
                state.itemsInCart.map(
                    // eslint-disable-next-line no-return-assign
                    (item) => {
                        let {productId} = item.omsData.salesItems[0];
                        const {productSizeId, subProducts} = item;
                        const jSubProd = JSON.stringify(subProducts);
                        return (
                            ((productId === payloadProdId) && productSizeId === payloadProdSizeId && jPaySubProd === jSubProd)
                                // eslint-disable-next-line no-param-reassign
                                ? item.amount += 1 : item.amount);
                    },
                );
            } else {
                // First item
                let item: ProductType = {
                    title: action.payload.title,
                    description: action.payload.description,
                    image: action.payload.image,
                    imageThumb: action.payload.imageThumb,
                    amount: 1,
                    omsData: action.payload.omsData,
                    subProducts: action.payload.subProducts,
                    linkedProducts: action.payload.linkedProducts,
                    productSizeId: payloadProdSizeId,
                    currentPrice: action.payload.currentPrice,
                    displayProductSize: action.payload.displayProductSize,
                    omsIds: [],
                };

                state.itemsInCart.push(item);
            }
            state.totalCartPrice = calculateTotal(state.itemsInCart);
            setKey("totalItems", state.totalItems.toString());
            setKey("itemsInCart", JSON.stringify(state.itemsInCart));
            setKey("totalCartPrice", state.totalCartPrice.toString());
        },
        removeItemsFromCart: (state: Draft<OrderState>, action: PayloadAction<ProductType>) => {
            // eslint-disable-next-line no-param-reassign
            state.totalItems -= 1;
            let isLastItem: boolean = true;
            let payloadProductId = action.payload.omsData.salesItems[0].productId;
            let payloadProductSizeId = action.payload.productSizeId;
            let payloadSubProd = action.payload.subProducts;
            let jPaySubProd = JSON.stringify(payloadSubProd);
            state.itemsInCart.map((item) => {
                let {productId} = item.omsData.salesItems[0];
                const {productSizeId, subProducts} = item;
                const jSubProd = JSON.stringify(subProducts);
                if (productId === payloadProductId && productSizeId === payloadProductSizeId && jPaySubProd === jSubProd) {
                    if (item.amount > 1 && state.totalItems > 1) {
                        // eslint-disable-next-line no-param-reassign
                        item.amount -= 1;
                        isLastItem = false;
                    }
                }
                return item;
            });

            if (isLastItem) {
                // eslint-disable-next-line no-param-reassign
                state.itemsInCart = state.itemsInCart.filter(
                    (val) => {
                        let {productId} = val.omsData.salesItems[0];
                        const {productSizeId, subProducts} = val;
                        const jSubProd = JSON.stringify(subProducts);
                        if ((productId === payloadProductId)
                            && (productSizeId === payloadProductSizeId) && (jPaySubProd === jSubProd)) {
                            if (val.amount > 1) {
                                val.amount -= 1;
                                return val;
                            }
                        } else if (productId !== payloadProductId) {
                            return val;
                        }
                        if ((productId === payloadProductId)
                            && (productSizeId !== payloadProductSizeId || jPaySubProd !== jSubProd)) {
                            return val;
                        }
                    }
                );
            }
            state.totalCartPrice = calculateTotal(state.itemsInCart);
            setKey("totalCartPrice", state.totalCartPrice.toString());
            setKey("totalItems", state.totalItems.toString());
            setKey("itemsInCart", JSON.stringify(state.itemsInCart));
        },
        submitFinalOrderPreValidate: (state, action: PayloadAction<OrderSubmitType>) => {
            state.finalOrder = action.payload;
        },
        changeOrderNumber: (state, action: PayloadAction<string>) => {
            state.orderNumber = action.payload;
        },
        updateEnterpriseUnitId: (state, action: PayloadAction<string>) => {
            state.enterpriseUnitId = action.payload;
        },
        changeOrderStatus: (state, action: PayloadAction<FinalOrderResponseType>) => {
            state.orderStatus = action.payload.status;
            if (!state.enterpriseUnitId) {
                state.enterpriseUnitId = action.payload?.location?.enterprise_unit;
            }
            state.appliedRewards = [];
            state.appliedRewards = state.appliedRewards.concat(action.payload.applied_rewards);
            state.finalOrder = {
                ...state.finalOrder,
                data: {
                    ...state.finalOrder.data,
                    Status: action.payload.status
                }
            };
        },
        setReOrderNumber:(state, action: PayloadAction<string>) => {
            state.reOrderNumber  = action.payload;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isOrderLoading = action.payload;
        },
        disableNavBarButtons: (state, action: PayloadAction<boolean>) => {
            state.navBarButtonDisable = action.payload;
        },
        changePrice: (state, action: PayloadAction<{ originalTotal?: number, totalPrice: number, tax: number, youPay: number }>) => {
            if (action.payload.originalTotal) {
                state.originalTotal = action.payload.originalTotal;  
            }
            state.totalPrice = action.payload.totalPrice;
            setKey("totalPrice", action.payload.totalPrice.toString());
            state.tax = action.payload.tax;
            state.youPay = action.payload.youPay;
        },
        resetOrder: () => {
            deleteKey("totalItems")
            deleteKey("itemsInCart")
            deleteKey("totalPrice")
            deleteKey("totalCartPrice")
            return {...initialState,
                        totalItems: 0,
                        itemsInCart: [],
                        totalPrice: 0,
                        totalCartPrice: 0,
                    };
        },
        removeGroupItemsFromCart: (state: Draft<OrderState>, action: PayloadAction<ProductType>) => {
            const {productId} = action.payload.omsData.salesItems[0];
            const {productSizeId, subProducts} = action.payload;
            const jSubProd = JSON.stringify(subProducts);

            const updatedCart = state.itemsInCart.filter((item) => {
                const {productId: itemProductId} = item.omsData.salesItems[0];
                const {productSizeId: itemProductSizeId, subProducts: itemSubProducts} = item;
                const jItemSubProd = JSON.stringify(itemSubProducts);
                return !(itemProductId === productId && itemProductSizeId === productSizeId && jItemSubProd === jSubProd);
            });

            const removedItems = state.itemsInCart.filter((item) => {
                const {productId: itemProductId} = item.omsData.salesItems[0];
                const {productSizeId: itemProductSizeId, subProducts: itemSubProducts} = item;
                const jItemSubProd = JSON.stringify(itemSubProducts);
                return itemProductId === productId && itemProductSizeId === productSizeId && jItemSubProd === jSubProd;
            });

            const removedItemsCount = removedItems.reduce((acc, item) => acc + item.amount, 0);

            state.totalItems -= removedItemsCount;
            state.totalCartPrice = calculateTotal(updatedCart);
            state.itemsInCart = updatedCart;

            setKey("totalCartPrice", state.totalCartPrice.toString());
            setKey("totalItems", state.totalItems.toString());
            setKey("itemsInCart", JSON.stringify(state.itemsInCart));
        },
        setGuestPaymentMethod: (state, action: PayloadAction<string>) => {
            state.paymentMethod = action.payload;
        },
        setGuestPaymentCardType: (state, action: PayloadAction<string>) => {
            state.paymentCardType = action.payload;
        },
    },
});

export default orderSlice.reducer;

// export methods
export const {
    setItemSelectedForEdit,
    addItemsToCart,
    removeItemsFromCart,
    removeGroupItemsFromCart,
    changeGroup,
    updateMenuGroups,
    resetOrder,
    submitFinalOrderPreValidate,
    changeOrderNumber,
    setReOrderNumber,
    changePrice,
    changeOrderStatus,
    disableNavBarButtons,
    updateAllGroups,
    setLoading,
    updateEnterpriseUnitId,
    setGuestPaymentMethod,
    setGuestPaymentCardType,
} = orderSlice.actions;
